import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';
import { getLocalDateString } from '../../utils/dateUtils';
import AppointmentSummaryModal from './AppointmentSummaryModal';
import NewAppointmentModal from './NewAppointmentModal';
import ServicesList from '../calendar/ServicesList';

const AppointmentsList = ({ filters, dateFilter, dateRange, onNewAppointment, onRefresh, availableProducts = [], availableAddons = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedGarage } = useGarage();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupedAppointments, setGroupedAppointments] = useState({});
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(new Set()); // Track which appointments are being updated
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  
  // Reschedule state
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // success, error, warning, info
  });

  // All possible status options
  const allStatusOptions = ['upcoming', 'ongoing', 'completed', 'cancelled', 'no_show'];

  // Get allowed status transitions based on current status (including current status)
  const getStatusOptions = (currentStatus) => {
    const status = currentStatus?.toLowerCase();
    
    switch (status) {
      case 'upcoming':
        return [
          { type: 'status', value: 'upcoming', label: 'Upcoming' },
          { type: 'status', value: 'ongoing', label: 'Ongoing' },
          { type: 'status', value: 'completed', label: 'Completed' },
          { type: 'status', value: 'no_show', label: 'No show' },
          { type: 'divider' },
          { type: 'action', value: 'reschedule', label: 'Reschedule' },
          { type: 'action', value: 'cancel', label: 'Cancel' }
        ];
      case 'ongoing':
        return [
          { type: 'status', value: 'ongoing', label: 'Ongoing' },
          { type: 'status', value: 'completed', label: 'Completed' },
          { type: 'status', value: 'no_show', label: 'No show' },
          { type: 'divider' },
          { type: 'action', value: 'reschedule', label: 'Reschedule' },
          { type: 'action', value: 'cancel', label: 'Cancel' }
        ];
      case 'completed':
      case 'cancelled':
      case 'no_show':
        // No options for final states - return empty array to disable dropdown
        return [];
      default:
        return [
          { type: 'status', value: 'upcoming', label: 'Upcoming' },
          { type: 'status', value: 'ongoing', label: 'Ongoing' },
          { type: 'status', value: 'completed', label: 'Completed' },  
          { type: 'status', value: 'no_show', label: 'No show' }
        ];
    }
  };

  // Fetch appointments
  const fetchAppointments = async () => {
    if (!selectedGarage?.id) return;
    
    setLoading(true);
    try {
      // Build API parameters based on filters and date selection
      const params = {
        garage_id: selectedGarage.id
      };

      // Only add non-empty filter parameters
      if (filters.customer_ids && filters.customer_ids.length > 0) {
        params.customer_ids = filters.customer_ids;
      }
      if (filters.product_ids && filters.product_ids.length > 0) {
        params.product_ids = filters.product_ids;
      }
      if (filters.addon_ids && filters.addon_ids.length > 0) {
        params.addon_ids = filters.addon_ids;
      }
      if (filters.payment_status && filters.payment_status !== '') {
        params.payment_status = convertToDbFormat(filters.payment_status);
      }
      if (filters.appointment_status && filters.appointment_status !== '') {
        params.status_filter = convertToDbFormat(filters.appointment_status);
      }

      // Add date parameters based on dateFilter
      if (dateFilter === 'upcoming') {
        // Fetch all appointments from today onwards (no end date to get all future appointments)
        const today = getLocalDateString(new Date());
        params.start_date = today;
        // Don't set end_date to get all future appointments
      } else if (dateFilter === 'custom' && dateRange) {
        params.start_date = getLocalDateString(dateRange.startDate);
        params.end_date = getLocalDateString(dateRange.endDate);
      }

      const response = await apiService.getBookings(params);
      const appointmentsList = response.data?.bookings || [];
      
      setAppointments(appointmentsList);
      groupAppointmentsByDate(appointmentsList);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
      setGroupedAppointments({});
    } finally {
      setLoading(false);
    }
  };

  // Group appointments by date
  const groupAppointmentsByDate = (appointmentsList) => {
    const grouped = appointmentsList.reduce((acc, appointment) => {
      const date = appointment.booking_date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(appointment);
      return acc;
    }, {});
    
    setGroupedAppointments(grouped);
  };

  // Handle appointment cancellation with reason
  const handleCancelAppointment = async () => {
    if (!appointmentToCancel || !cancelReason.trim()) {
      showSnackbar('Please provide a cancellation reason', 'error');
      return;
    }

    try {
      // Add appointment to loading state
      setUpdatingStatus(prev => new Set(prev).add(appointmentToCancel));

      // Call API to cancel booking with reason
      await apiService.cancelBooking(appointmentToCancel, cancelReason.trim());

      // Show success notification
      showSnackbar('Appointment cancelled successfully', 'success');

      // Refresh the appointments list
      await fetchAppointments();

      // Close dialog and reset state
      setCancelDialogOpen(false);
      setCancelReason('');
      setAppointmentToCancel(null);

    } catch (error) {
      console.error('Error cancelling appointment:', error);
      
      // Show error notification
      const errorMessage = error.response?.data?.detail || 'Failed to cancel appointment';
      showSnackbar(errorMessage, 'error');
    } finally {
      // Remove appointment from loading state
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(appointmentToCancel);
        return newSet;
      });
    }
  };

  // Handle status change or action
  const handleStatusChange = async (appointmentId, selection) => {
    try {
      // Find the current appointment to check if status is the same
      const currentAppointment = appointments.find(apt => apt.booking_id === appointmentId);
      
      // If the selection is the same as current status, do nothing
      if (currentAppointment && currentAppointment.status?.toLowerCase() === selection?.toLowerCase()) {
        return;
      }
      
      if (selection === 'reschedule') {
        // Open reschedule modal with just the booking ID
        setAppointmentToReschedule(appointmentId);
        setRescheduleModalOpen(true);
        return;
      }
      
      if (selection === 'cancel') {
        // Open cancel dialog to get reason
        setAppointmentToCancel(appointmentId);
        setCancelDialogOpen(true);
        return;
      }
      
      // Add appointment to loading state
      setUpdatingStatus(prev => new Set(prev).add(appointmentId));
      
      const statusToUpdate = selection;
      const actionMessage = 'Appointment status changed successfully';

      // Call API to update booking status
      await apiService.updateBookingStatus(appointmentId, statusToUpdate);
      
      // Update the appointment status in the local state immediately
      setAppointments(prevAppointments => 
        prevAppointments.map(apt => 
          apt.booking_id === appointmentId 
            ? { ...apt, status: statusToUpdate }
            : apt
        )
      );
      
      // Re-group appointments to reflect the status change
      setGroupedAppointments(prevGrouped => {
        const updatedAppointments = Object.values(prevGrouped).flat().map(apt => 
          apt.booking_id === appointmentId 
            ? { ...apt, status: statusToUpdate }
            : apt
        );
        const grouped = updatedAppointments.reduce((acc, appointment) => {
          const date = appointment.booking_date;
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(appointment);
          return acc;
        }, {});
        return grouped;
      });
      
      // Show success notification
      showSnackbar(actionMessage, 'success');
      
    } catch (error) {
      console.error('Error updating appointment:', error);
      
      // Show error notification
      const errorMessage = error.response?.data?.detail || 'Failed to update appointment status';
      showSnackbar(errorMessage, 'error');
    } finally {
      // Remove appointment from loading state
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(appointmentId);
        return newSet;
      });
    }
  };

  // Format date for display
  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    }
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'free': return 'info';
      case 'due': return 'warning';
      case 'payment failure': return 'error';
      default: return 'default';
    }
  };

  // Get appointment status color
  const getAppointmentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'upcoming': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Get status dot color (actual color values for dots) - only for status, not actions
  const getStatusDotColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'upcoming': return '#1976d2'; // Blue
      case 'ongoing': return '#ff9800'; // Orange
      case 'completed': return '#2e7d32'; // Green
      case 'cancelled': return '#d32f2f'; // Red
      case 'no_show': return '#9c27b0'; // Purple
      default: return '#9e9e9e'; // Gray
    }
  };

  // Format status for display
  const formatStatusDisplay = (status) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
      case 'no_show':
        return 'No show';
      case 'upcoming':
        return 'Upcoming';
      case 'ongoing':
        return 'Ongoing';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Format payment status for display
  const formatPaymentStatusDisplay = (status) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
      case 'paid':
        return 'Paid';
      case 'due':
        return 'Due';
      case 'free':
        return 'Free';
      case 'payment failure':
        return 'Payment failure';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Convert display format back to database format for API calls
  const convertToDbFormat = (displayValue) => {
    if (!displayValue) return '';
    return displayValue === 'No show' ? 'no_show' : displayValue.toLowerCase();
  };


  // Get status color for chips (same as AppointmentSummaryModal)
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'upcoming': return 'primary';
      case 'ongoing': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'no_show': return 'error';
      default: return 'default';
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle reschedule success
  const handleRescheduleSuccess = () => {
    setRescheduleModalOpen(false);
    setAppointmentToReschedule(null);
    showSnackbar('Appointment rescheduled successfully', 'success');
    fetchAppointments();
  };


  // Handle reschedule error
  const handleRescheduleError = (errorMessage) => {
    showSnackbar(errorMessage || 'Failed to reschedule appointment', 'error');
  };


  useEffect(() => {
    fetchAppointments();
  }, [selectedGarage?.id, filters, dateFilter, dateRange]);

  // Expose fetchAppointments to parent component
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchAppointments);
    }
  }, [onRefresh]);

  // Render empty state
  const renderEmptyState = () => (
    <Box sx={{ 
      textAlign: 'center', 
      py: 8,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      <CalendarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
      <Typography variant="h6" color="text.secondary">
        No appointments
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onNewAppointment}
        sx={{ 
          mt: 2,
          borderRadius: 1,
          textTransform: 'none',
          fontWeight: 500,
          px: 3,
          py: 1
        }}
      >
        New Appointment
      </Button>
    </Box>
  );

  // Render mobile card view
  const renderMobileView = () => (
    <Box>
      {Object.entries(groupedAppointments).map(([date, dayAppointments]) => (
        <Box key={date} sx={{ mb: 3 }}>
          {/* Date Header */}
          <Box sx={{
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(25, 118, 210, 0.08)' 
              : 'rgba(25, 118, 210, 0.04)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
            p: { xs: 2, md: 2.5 },
            mb: 2
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarIcon sx={{ 
                  color: 'primary.main', 
                  fontSize: { xs: 18, md: 20 } 
                }} />
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: { xs: '0.9rem', md: '1rem' }
                  }}
                >
                  {formatDateDisplay(date)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                px: { xs: 1.5, md: 2 },
                py: { xs: 0.5, md: 0.75 },
                borderRadius: { xs: 1.5, md: 2 },
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                fontWeight: 600
              }}>
                <Typography variant="body2" sx={{ 
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  color: 'inherit'
                }}>
                  {dayAppointments.length}
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontSize: 'inherit',
                  color: 'inherit'
                }}>
                  Appointment{dayAppointments.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Appointment Cards */}
          <Stack spacing={2}>
            {dayAppointments.map((appointment) => (
              <Card 
                key={appointment.booking_id}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 2 }
                }}
                onClick={() => {
                  setSelectedAppointment(appointment);
                  setSummaryModalOpen(true);
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="500">
                        {appointment.booking_time}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {appointment.booking_ref}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <CarIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Box sx={{
                      backgroundColor: '#FEDE00', // UK number plate yellow
                      border: '3px solid #000000', // Black border
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      minWidth: '75px',
                      textAlign: 'center',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                        : '0 2px 4px rgba(0, 0, 0, 0.2)'
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{
                          fontWeight: 900,
                          fontSize: '0.75rem',
                          color: '#000000', // Black text
                          letterSpacing: '1px',
                          fontFamily: 'monospace',
                          textTransform: 'uppercase'
                        }}
                      >
                        {appointment.vehicle_regno}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Services
                    </Typography>
                    <ServicesList 
                      services={[
                        ...(appointment.products?.map(product => ({
                          name: product.custom_name || product.name,
                          category: product.category
                        })) || []),
                        ...(appointment.addons?.map(addon => ({
                          name: addon.custom_name || addon.name,
                          category: addon.category
                        })) || [])
                      ]}
                      variant="list"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {appointment.customer_name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={formatPaymentStatusDisplay(appointment.payment_status)} 
                        size="small" 
                        color={getPaymentStatusColor(appointment.payment_status)}
                      />
                      <Typography variant="body2" fontWeight="500">
                        £{appointment.total_amount || '0.00'}
                      </Typography>
                    </Box>
                    
                    <Box
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(appointment.status?.toLowerCase() === 'completed' || appointment.status?.toLowerCase() === 'cancelled' || appointment.status?.toLowerCase() === 'no_show') ? (
                        <Chip
                          label={formatStatusDisplay(appointment.status)}
                          color={getStatusColor(appointment.status)}
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : updatingStatus.has(appointment.booking_id) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 100, py: 1 }}>
                          <CircularProgress 
                            size={16} 
                            color="primary"
                          />
                        </Box>
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={appointment.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(appointment.booking_id, e.target.value);
                            }}
                            disabled={updatingStatus.has(appointment.booking_id)}
                            displayEmpty
                            renderValue={(value) => formatStatusDisplay(value)}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                fontSize: '0.875rem',
                                color: getStatusDotColor(appointment.status)
                              }
                            }}
                          >
                            {getStatusOptions(appointment.status).map((option, index) => {
                              if (option.type === 'divider') {
                                return <Divider key={index} sx={{ my: 0.5 }} />;
                              }
                              if (option.type === 'action') {
                                return (
                                  <MenuItem key={index} value={option.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: option.value === 'reschedule' ? '#1976d2' : '#d32f2f',
                                          flexShrink: 0
                                        }}
                                      />
                                      {option.label}
                                    </Box>
                                  </MenuItem>
                                );
                              }
                              return (
                                <MenuItem 
                                  key={index} 
                                  value={option.value}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: getStatusDotColor(option.value),
                                        flexShrink: 0
                                      }}
                                    />
                                    {option.label}
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );

  // Render desktop table view
  const renderDesktopView = () => {
    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: { xs: 2, md: 3 },
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
            : '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
          overflow: 'hidden'
        }}
      >
        <Table sx={{ minWidth: { xs: 650, lg: 900 } }}>
          <TableHead>
            <TableRow sx={{ 
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.02)' 
                : 'rgba(0, 0, 0, 0.02)',
              borderBottom: '2px solid',
              borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'
            }}>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                TIME
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                BOOKING ID
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                VEHICLE
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                SERVICES
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                CUSTOMER
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                PAYMENT
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                PRICE
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                py: { xs: 2, md: 2.5 },
                px: { xs: 1.5, md: 2 },
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupedAppointments)
              .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
              .map(([date, dayAppointments]) => (
                <React.Fragment key={date}>
                  {/* Date Group Header Row */}
                  <TableRow sx={{
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(25, 118, 210, 0.08)' 
                      : 'rgba(25, 118, 210, 0.04)',
                    borderBottom: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'
                  }}>
                    <TableCell 
                      colSpan={8} 
                      sx={{ 
                        py: { xs: 1.5, md: 2 },
                        px: { xs: 1.5, md: 2 },
                        borderBottom: 'none'
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CalendarIcon sx={{ 
                            color: 'primary.main', 
                            fontSize: { xs: 18, md: 20 } 
                          }} />
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'primary.main',
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }}
                          >
                            {formatDateDisplay(date)}
                          </Typography>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          px: { xs: 1.5, md: 2 },
                          py: { xs: 0.5, md: 0.75 },
                          borderRadius: { xs: 1.5, md: 2 },
                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                          fontWeight: 600
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            color: 'inherit'
                          }}>
                            {dayAppointments.length}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontSize: 'inherit',
                            color: 'inherit'
                          }}>
                            Appointment{dayAppointments.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Appointment Rows */}
                  {dayAppointments.map((appointment, appointmentIndex) => (
                    <TableRow 
                      key={appointment.booking_id}
                      sx={{ 
                        cursor: 'pointer',
                        backgroundColor: appointmentIndex % 2 === 0 
                          ? 'transparent' 
                          : theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.01)' 
                            : 'rgba(0, 0, 0, 0.01)',
                        borderBottom: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                      }}
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setSummaryModalOpen(true);
                      }}
                    >
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon sx={{ 
                            fontSize: { xs: 16, md: 18 }, 
                            color: 'primary.main',
                            flexShrink: 0 
                          }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                              color: 'text.primary'
                            }}
                          >
                            {appointment.booking_time}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'primary.main',
                            fontWeight: 500,
                            fontSize: { xs: '0.8rem', md: '0.875rem' }
                          }}
                        >
                          {appointment.booking_ref}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CarIcon sx={{ 
                            fontSize: { xs: 18, md: 20 }, 
                            color: 'primary.main',
                            flexShrink: 0 
                          }} />
                          <Box sx={{
                            backgroundColor: '#FEDE00',
                            border: '3px solid #000000',
                            borderRadius: { xs: 1, md: 1.5 },
                            px: { xs: 1.5, md: 2 },
                            py: { xs: 0.5, md: 0.75 },
                            minWidth: { xs: '80px', md: '90px' },
                            textAlign: 'center',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                              : '0 2px 4px rgba(0, 0, 0, 0.2)'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 900,
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                color: '#000000',
                                letterSpacing: '1px',
                                fontFamily: 'monospace',
                                textTransform: 'uppercase'
                              }}
                            >
                              {appointment.vehicle_regno}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <ServicesList 
                          services={[
                            ...(appointment.products?.map(product => ({
                              name: product.custom_name || product.name,
                              category: product.category
                            })) || []),
                            ...(appointment.addons?.map(addon => ({
                              name: addon.custom_name || addon.name,
                              category: addon.category
                            })) || [])
                          ]}
                          variant="compact"
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ 
                            fontSize: { xs: 16, md: 18 }, 
                            color: 'text.secondary',
                            flexShrink: 0 
                          }} />
                          <Typography 
                            variant="body2"
                            sx={{
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                              color: 'text.primary'
                            }}
                          >
                            {appointment.customer_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <Chip 
                          label={formatPaymentStatusDisplay(appointment.payment_status)} 
                          size="small" 
                          color={getPaymentStatusColor(appointment.payment_status)}
                          sx={{
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: '24px', md: '28px' },
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 1.5, md: 2 }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            color: 'text.primary'
                          }}
                        >
                          £{appointment.total_amount || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        sx={{ 
                          py: { xs: 2, md: 2.5 },
                          px: { xs: 1.5, md: 2 }
                        }}
                      >
                        {(appointment.status?.toLowerCase() === 'completed' || appointment.status?.toLowerCase() === 'cancelled' || appointment.status?.toLowerCase() === 'no_show') ? (
                          <Chip
                            label={formatStatusDisplay(appointment.status)}
                            color={getStatusColor(appointment.status)}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        ) : updatingStatus.has(appointment.booking_id) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 100, py: 1 }}>
                            <CircularProgress 
                              size={16} 
                              color="primary"
                            />
                          </Box>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={appointment.status}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(appointment.booking_id, e.target.value);
                              }}
                              disabled={updatingStatus.has(appointment.booking_id)}
                              displayEmpty
                              renderValue={(value) => formatStatusDisplay(value)}
                              sx={{
                                '& .MuiOutlinedInput-input': {
                                  py: 0.5,
                                  fontSize: '0.875rem',
                                  color: getStatusDotColor(appointment.status)
                                }
                              }}
                            >
                              {getStatusOptions(appointment.status).map((option, index) => {
                                if (option.type === 'divider') {
                                  return <Divider key={index} sx={{ my: 0.5 }} />;
                                }
                                if (option.type === 'action') {
                                  return (
                                    <MenuItem key={index} value={option.value}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: option.value === 'reschedule' ? '#1976d2' : '#d32f2f',
                                            flexShrink: 0
                                          }}
                                        />
                                        {option.label}
                                      </Box>
                                    </MenuItem>
                                  );
                                }
                                return (
                                  <MenuItem 
                                    key={index} 
                                    value={option.value}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: getStatusDotColor(option.value),
                                          flexShrink: 0
                                        }}
                                      />
                                      {option.label}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading appointments...</Typography>
      </Box>
    );
  }

  if (Object.keys(groupedAppointments).length === 0) {
    return renderEmptyState();
  }

  return (
    <Box>
      {isMobile ? renderMobileView() : renderDesktopView()}
      
      <AppointmentSummaryModal
        open={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        appointment={selectedAppointment}
        onDataChange={fetchAppointments}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
      />

      {/* Cancel Appointment Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => {
          setCancelDialogOpen(false);
          setCancelReason('');
          setAppointmentToCancel(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for cancelling this appointment:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter cancellation reason..."
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelDialogOpen(false);
              setCancelReason('');
              setAppointmentToCancel(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCancelAppointment}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim() || updatingStatus.has(appointmentToCancel)}
          >
            {updatingStatus.has(appointmentToCancel) ? 'Cancelling...' : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Appointment Modal */}
      <NewAppointmentModal
        open={rescheduleModalOpen}
        onClose={() => {
          setRescheduleModalOpen(false);
          setAppointmentToReschedule(null);
        }}
        garageId={selectedGarage?.id}
        onSuccess={handleRescheduleSuccess}
        onError={handleRescheduleError}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
        rescheduleMode={true}
        rescheduleBookingId={appointmentToReschedule}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppointmentsList;